import { EyeIcon, EyeSlashIcon, UserPlusIcon } from '@phosphor-icons/react'
import { useTranslation } from 'react-i18next'
import { Outlet } from 'react-router-dom'

import { Page } from '@/components/layout/page'
import { PageHeader } from '@/components/layout/page-header'
import { Button } from '@/components/ui/button'
import { Icon } from '@/components/ui/icon'
import { NavbarLink, NavbarMenu } from '@/components/ui/navbar-menu'
import { ToggleSwitch } from '@/components/ui/toggle-switch'
import LegacyVisitors from '@/components/Visitors/Visitors'
import { popupFactory } from '@/factories'
import { useLocalStorage } from '@/hooks'
import { GuestModel } from '@/models'

import { AddVisitorModal } from './add-visitor-modal'
import { VisitorTypeEnum } from './add-visitor-modal/add-visitor-modal.types'
import { VisitorsAnalytics } from './visitors-analytics'
import { VISITORS_ANALYTICS_STORAGE_KEY } from './visitors-analytics/visitors-analytics.constants'
import { getVisitorsNavItems, VISITORS_UI_VERSION_KEY } from './visitors-dashboard.page.constants'
import { UiVersion } from './visitors-dashboard.page.types'

export type { VisitorsDashboardOutletContext } from './visitors-dashboard.page.types'

export const VisitorsDashboardPage = () => {
  const { t } = useTranslation(['visitors'])
  const [isAnalyticsOpen, setIsAnalyticsOpen] = useLocalStorage(
    VISITORS_ANALYTICS_STORAGE_KEY,
    true,
  )

  // Note: UI version defining which design is used - legacy or new
  const [uiVersion, setUiVersion] = useLocalStorage<UiVersion>(VISITORS_UI_VERSION_KEY, 'new')

  const handleUIToggle = () => {
    setUiVersion(uiVersion === 'new' ? 'legacy' : 'new')
  }

  const designSwitch = (
    <ToggleSwitch
      falsyLabel={t('visitors:dashboard.designToggle.old')}
      truthyLabel={t('visitors:dashboard.designToggle.new')}
      checked={uiVersion === 'new'}
      onChange={handleUIToggle}
    />
  )

  const handleAddVisitor = () => {
    popupFactory.open(AddVisitorModal, {})
  }

  const handleEditGuest = (guest: GuestModel) => {
    popupFactory.open(AddVisitorModal, {
      defaultVisitorType: VisitorTypeEnum.enum.guest,
      editingVisitor: guest,
    })
  }

  const handleEditVendor = (vendor: GuestModel) => {
    popupFactory.open(AddVisitorModal, {
      defaultVisitorType: VisitorTypeEnum.enum.vendor,
      editingVisitor: vendor,
    })
  }

  if (uiVersion === 'legacy') {
    return <LegacyVisitors designSwitch={designSwitch} uiVersion={uiVersion} />
  }

  return (
    <Page>
      <PageHeader
        title={t('visitors:toolbar.title')}
        titleExtra={
          <Button size="small" use="secondary" onClick={() => setIsAnalyticsOpen(!isAnalyticsOpen)}>
            <Icon icon={isAnalyticsOpen ? EyeSlashIcon : EyeIcon} />
            {isAnalyticsOpen
              ? t('visitors:dashboard.analytics.hideAnalytics')
              : t('visitors:dashboard.analytics.showAnalytics')}
          </Button>
        }
        actions={
          <>
            {designSwitch}
            <Button size="small" onClick={handleAddVisitor}>
              {t('visitors:dashboard.actions.addNew')}
              <Icon icon={UserPlusIcon} />
            </Button>
          </>
        }
      />

      {isAnalyticsOpen && <VisitorsAnalytics />}

      <NavbarMenu>
        {getVisitorsNavItems(t)
          .filter((item) => !item.hidden)
          .map((item) => (
            <NavbarLink key={item.to.path} to={item.to}>
              {item.label}
            </NavbarLink>
          ))}
      </NavbarMenu>

      <Outlet
        context={{
          onEditGuest: handleEditGuest,
          onEditVendor: handleEditVendor,
          uiVersion,
        }}
      />
    </Page>
  )
}
